import React from 'react';
import type { CustomTooltipProps } from '@ag-grid-community/react';

import { ReadonlyTextarea } from '../../form/Textarea/ReadonlyTextarea';

// AG-Grid instantiates this with the full ITooltipParams; we only need the
// resolved cell value (set via the column's tooltipValueGetter in DataGrid).
//
// **No `format` prop, and it had one.** It existed only to pass `formatting` through to
// `ReadonlyTextarea`, which still accepts that prop - so dropping it here does not remove
// the choice, it takes the default: `RawTextarea` defaults to `formatting="legacy"`.
// Legacy renders a single newline as a paragraph gap (an empty block between the lines);
// `modern` would render it as a plain line break. Grid cells carry plain API text, so the
// only visible consequence is the extra vertical space in a height-capped tooltip.
export type DataGridTooltipProps = Pick<
  CustomTooltipProps<unknown, string>,
  'value'
>;

export const DataGridTooltip = ({ value }: DataGridTooltipProps) => {
  if (!value) {
    return null;
  }

  // Incoming text may carry escaped newline sequences — the literal characters
  // backslash-n / backslash-r-n rather than real newline characters — which
  // commonly happens when a value is serialized/JSON-encoded more than once
  // upstream. Those escapes have no meaning to the renderer and would display
  // verbatim, so un-escape them one-for-one into real newlines. Every real newline
  // then renders as a paragraph gap rather than a line break, because the tooltip
  // takes `RawTextarea`'s `legacy` default (see above) - three lines come out as
  // five blocks, one empty between each pair, and a doubled newline doubles the
  // gap. No-op when the value already contains real newlines.
  const normalizedValue = value.replaceAll(/\\r\\n|\\r|\\n/g, '\n');

  return (
    <div className="ag-tooltip-axiom" data-test="DATA_GRID_TOOLTIP">
      <ReadonlyTextarea name="DATA_GRID_TOOLTIP_TEXT" value={normalizedValue} />
    </div>
  );
};
